/**
 * Markdown to data, shared by everything that shows a post.
 *
 * ⚠️ **One parser, two readers, on purpose.** The admin's editor previews with
 * this and the website renders published posts with it, so what a writer sees
 * while typing is what a reader is shown. Two copies would let the two drift,
 * and the drift would be invisible until somebody complained that their list
 * came out as a paragraph.
 *
 * It lives here rather than in either app because `libs/shared-types` is
 * already what the admin, the website and the API agree through — locales,
 * feature icons, and now this.
 *
 * ⚠️ **Its tests live in `apps/backend/src/lib/markdown.spec.ts`**, where this
 * code used to. `libs/shared-types` has a `typecheck` target and no `test`
 * one, so a spec placed beside this file would be collected by nothing and
 * reported by nobody — a suite that never runs, which is worse than a suite
 * somewhere unexpected. The backend imports this through its own
 * `lib/markdown`, so those tests still drive this parser.
 *
 * Returns **data, not HTML**, and that is the security decision. The design's
 * own preview built an HTML string and handed it to `dangerouslySetInnerHTML`;
 * this returns spans each side renders as its own elements, so a post that
 * contains `<script>` is text on the way in and text on the way out, with no
 * escaping step for anybody to forget.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string };

/** How a table column lines up, from the `:` on its separator; null is unsaid. */
export type Align = 'left' | 'center' | 'right' | null;

export type Block =
  | { kind: 'heading'; level: number; spans: Inline[] }
  | { kind: 'paragraph'; spans: Inline[] }
  | { kind: 'quote'; spans: Inline[] }
  | { kind: 'bullets'; items: Inline[][] }
  | { kind: 'numbers'; items: Inline[][] }
  | { kind: 'code'; text: string }
  | { kind: 'table'; header: Inline[][]; align: Align[]; rows: Inline[][][] };

/**
 * A GitHub-style table: a row of cells between pipes, a separator of dashes
 * under it, then rows. The separator is what makes it a table — a line with
 * pipes and nothing under it is a paragraph that happens to contain pipes,
 * which is what such a line used to be *with* a separator too: a price table
 * pasted into a post came out as one run of `| $1,199 | $1,399 |`.
 */
const SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
/** Whether a line is a table's separator row — drawn, never read as words. */
export const isTableSeparator = (line: string): boolean => SEPARATOR.test(line);
const isRow = (line: string): boolean => line.includes('|') && line.trim() !== '';

/** The cells of one row, outer pipes dropped, `\|` inside a cell kept as a pipe. */
const cellsOf = (line: string): string[] => {
  let text = line.trim();
  if (text.startsWith('|')) text = text.slice(1);
  if (text.endsWith('|') && !text.endsWith('\\|')) text = text.slice(0, -1);
  return text.split(/(?<!\\)\|/).map((cell) => cell.replace(/\\\|/g, '|').trim());
};

const alignOf = (cell: string): Align => {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
};

/**
 * The table that starts at `lines[at]`, and where it ends — or null when no
 * table starts there. A short row is padded and a long one cut to the
 * header's width, the way GitHub does it, so a cell typed one pipe short
 * does not shift the columns after it.
 */
const tableAt = (lines: string[], at: number): { block: Block; end: number } | null => {
  const head = lines[at];
  const rule = lines[at + 1];
  if (head === undefined || rule === undefined || !isRow(head) || !SEPARATOR.test(rule))
    return null;
  const header = cellsOf(head);
  const align = cellsOf(rule).map(alignOf);
  if (align.length !== header.length) return null;

  const width = header.length;
  const fit = (cells: string[]): Inline[][] =>
    Array.from({ length: width }, (_, index) => parseInline(cells[index] ?? ''));

  const rows: Inline[][][] = [];
  let end = at + 2;
  while (end < lines.length && isRow(lines[end])) {
    rows.push(fit(cellsOf(lines[end])));
    end += 1;
  }
  return { block: { kind: 'table', header: fit(header), align, rows }, end };
};

/**
 * Splits one line into styled runs.
 *
 * Returns data, not HTML. The design's own preview builds an HTML string and
 * hands it to `dangerouslySetInnerHTML`; this returns spans the page renders as
 * React, so a post that contains `<script>` is text on the way in and text on
 * the way out, with no escaping step that somebody can forget to do.
 */
export const parseInline = (line: string): Inline[] => {
  /*
   * Emphasis needs a non-space either side of the marker, which is what
   * Markdown itself requires. Without that guard `2 * 3 * 4` becomes an italic
   * " 3 " — arithmetic in a post, silently restyled.
   */
  const pattern =
    /(\*\*(?!\s)[^*\n]+?(?<!\s)\*\*|\*(?!\s)[^*\n]+?(?<!\s)\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\))/g;
  const spans: Inline[] = [];
  let at = 0;

  for (const match of line.matchAll(pattern)) {
    const found = match[0];
    const index = match.index ?? 0;
    if (index > at) spans.push({ kind: 'text', text: line.slice(at, index) });

    if (found.startsWith('**')) spans.push({ kind: 'bold', text: found.slice(2, -2) });
    else if (found.startsWith('`')) spans.push({ kind: 'code', text: found.slice(1, -1) });
    else if (found.startsWith('[')) {
      const split = found.indexOf('](');
      spans.push({
        kind: 'link',
        text: found.slice(1, split),
        href: found.slice(split + 2, -1),
      });
    } else spans.push({ kind: 'italic', text: found.slice(1, -1) });

    at = index + found.length;
  }

  if (at < line.length) spans.push({ kind: 'text', text: line.slice(at) });
  return spans;
};

/** Groups the lines into blocks: headings, lists, quotes, fences, paragraphs. */
export const parseMarkdown = (source: string): Block[] => {
  const blocks: Block[] = [];
  const lines = source.split('\n');

  let paragraph: string[] = [];
  let bullets: string[] = [];
  let numbers: string[] = [];
  let quote: string[] = [];
  let fence: string[] | null = null;

  const flush = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', spans: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
    if (bullets.length) {
      blocks.push({ kind: 'bullets', items: bullets.map(parseInline) });
      bullets = [];
    }
    if (numbers.length) {
      blocks.push({ kind: 'numbers', items: numbers.map(parseInline) });
      numbers = [];
    }
    if (quote.length) {
      blocks.push({ kind: 'quote', spans: parseInline(quote.join(' ')) });
      quote = [];
    }
  };

  for (let at = 0; at < lines.length; at += 1) {
    const line = lines[at];
    if (/^\s*```/.test(line)) {
      if (fence === null) {
        flush();
        fence = [];
      } else {
        blocks.push({ kind: 'code', text: fence.join('\n') });
        fence = null;
      }
      continue;
    }
    if (fence !== null) {
      fence.push(line);
      continue;
    }

    const table = tableAt(lines, at);
    if (table) {
      flush();
      blocks.push(table.block);
      at = table.end - 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: 'heading', level: heading[1].length, spans: parseInline(heading[2]) });
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet) {
      if (paragraph.length || numbers.length || quote.length) flush();
      bullets.push(bullet[1]);
      continue;
    }

    const number = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (number) {
      if (paragraph.length || bullets.length || quote.length) flush();
      numbers.push(number[1]);
      continue;
    }

    const quoted = /^\s*>\s?(.*)$/.exec(line);
    if (quoted) {
      if (paragraph.length || bullets.length || numbers.length) flush();
      quote.push(quoted[1]);
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    if (bullets.length || numbers.length || quote.length) flush();
    paragraph.push(line);
  }

  // An unclosed fence is still a code block: the writer is mid-typing, and
  // reflowing everything below it as prose is worse than showing it as code.
  if (fence !== null) blocks.push({ kind: 'code', text: fence.join('\n') });
  flush();

  return blocks;
};
